
import { Component, Vue, Watch } from 'vue-property-decorator';
import RepositoryFactory from '@/api/RepositoryFactory';
import Header from '@/components/organisms/Header.vue';
import Notice from '@/components/organisms/Notice.vue';
import Tabs from '@/components/molecules/Tabs.vue';
import DamPrediction from '@/components/organisms/tabs/DamPrediction.vue';
import DamPredictionTable from '@/components/organisms/tabs/DamPredictionTable.vue';
import DamPredictionMobile from '@/components/organisms/tabs/DamPredictionMobile.vue';
import DamEnsample from '@/components/organisms/tabs/DamEnsample.vue';
import DamEnsampleMobile from '@/components/organisms/tabs/DamEnsampleMobile.vue';
import DamCompareWater from '@/components/organisms/tabs/DamCompareWater.vue';
import DamCompareWaterMobile from '@/components/organisms/tabs/DamCompareWaterMobile.vue';
import MeshRain from '@/components/organisms/tabs/MeshRain.vue';
import GraphSetting from '@/components/organisms/GraphSetting.vue';
import Spinner from 'vue-spinner/src/ScaleLoader.vue';
import { formatDateTimeToYYYYMMDDHHMM, getCookieValue } from '@/functions';
import { loadKokiFloodReleaseSupportInfo } from '@/state/kokiFloodReleaseSupportInfo';
import { ProjectKey } from '@/types/projectKey';
import type { TabId } from '@/types/tabId';
import { LogAction } from '@/types/logAction';
import logRepository from '@/api/logRepository';
import { ContentsId, useStateStore } from '@/shared/model';
import {
  useTelemeterStore, useTelemeterBasinStore, useSimTableStore, useSysInfoStore,
} from '@/shared/service';
import { CustomCalculation } from '@/pages/customCalculation';
import { useResultStore } from '@/shared/service/result';

const tableInfoRepository = RepositoryFactory.getTableInfo;
const lastTimeRepository = RepositoryFactory.lastTime;
const authRepository = RepositoryFactory.auth;
const dischargeInfoRepository = RepositoryFactory.damDischargeInfo;
const damSupportInfoRepository = RepositoryFactory.damSupportInfo;
const kurihashiKakuhoRepository = RepositoryFactory.getKurihashiKakuho;

export type Tab = {
  id: TabId;
  title: string;
  component: any;
  style?: any;
}

@Component({
  components: {
    Header,
    Notice,
    Tabs,
    DamPrediction,
    DamPredictionMobile,
    GraphSetting,
    Spinner,
  },
})
export default class Home extends Vue {
  private skipWatch = true;

  private sysInfoStore = useSysInfoStore();

  marginTop = '0px';

  /** result読み込み時に音声再生をしてもよいかどうか */
  private canPlaySound = false;

  get loading() {
    return this['$store'].getters.loading;
  }

  set loading(value) {
    this['$store'].commit('setLoading', value);
  }

  get header() {
    return this['$store'].getters.header;
  }

  get isJizenHouryu() {
    return this['$store'].getters.isJizenHouryu;
  }

  get showDrawer() {
    return this['$store'].getters.showDrawer;
  }

  set showDrawer(value) {
    this['$store'].commit('setShowDrawer', value);
  }

  get showEnsample() {
    return this.sysInfoStore.showEnsample;
  }

  get tabs(): Tab[] {
    return this.contents.map((content) => {
      const base = { id: content.id, title: content.tabName };

      switch (content.id) {
        case ContentsId.AutoCalculation:
          return { component: this.isMobile ? DamPredictionMobile : DamPrediction, ...base };
        case ContentsId.EnsembleCalculation:
          return { component: this.isMobile ? DamEnsampleMobile : DamEnsample, ...base };
        case ContentsId.ManualCalculation:
          return { component: this.isMobile ? DamPredictionMobile : DamPrediction, ...base };
        case ContentsId.MultiStations:
          return { component: this.isMobile ? DamCompareWaterMobile : DamCompareWater, ...base };
        case ContentsId.MeshMap:
          return { component: MeshRain, style: { height: '80vh' }, ...base };
        case ContentsId.StatusChart:
          return { component: DamPredictionTable, ...base };
        case ContentsId.CustomCalculation:
          return { component: CustomCalculation, ...base };
        default:
          throw new Error('unexpected error occurred.');
      }
    });
  }

  get basinId() {
    return this['$store'].getters.basinId;
  }

  get ensampleTabStatusStatus() {
    return this['$store'].getters.ensampleTabStatusStatus;
  }

  get stationId() {
    return this['$store'].getters.stationId;
  }

  set stationId(value) {
    this['$store'].commit('setStationId', value);
  }

  get targetTime() {
    return this['$store'].getters.formatTargetTime;
  }

  get enTargetTime() {
    return this['$store'].getters.enFormatTargetTime;
  }

  get yskSpan() {
    return this['$store'].getters.yskSpan;
  }

  get tabId(): TabId {
    return this['$store'].getters.tabId;
  }

  set tabId(value) {
    this['$store'].commit('tabId', value);
  }

  get autoLoad() {
    return this['$store'].getters.autoLoad;
  }

  set autoLoad(value) {
    this['$store'].commit('setAutoLoad', value);
  }

  get contents(): any[] {
    return this['$store'].getters.contents;
  }

  get currentStationIds(): string[] {
    return this['$store'].getters.currentStationIds;
  }

  get isMobile(): boolean {
    return window.innerWidth <= 1200;
  }

  get forceUpdateResult(): boolean {
    const stateStore = useStateStore();
    return stateStore.forceUpdateResult;
  }

  get resultStore() {
    return useResultStore();
  }

  /**
   * 初期stationIdを取得
   * 優先度
   * 1.cookieのbasinIdSettingsの対応するbasinIdの値
   * 2.cookieのobserIdの値
   * 3.sysInfoのstationsから配列の最初のstationId
   */
  getDefaultStationId(currentStationIds: string[], basinId: string): string {
    const jsonBasinIdSettings = getCookieValue('basinIdSettings');
    if (jsonBasinIdSettings) {
      if (jsonBasinIdSettings) {
        const basinIdSettings = JSON.parse(
          jsonBasinIdSettings.replace(/'/g, '"').replace(/\\"/g, '"').replace(/\\054/g, ',').slice(1, -1),
        );
        // basinIdが一致する設定を取得
        const basinIdSetting = basinIdSettings.find((setting) => setting['basinId'] === basinId);
        // obserIdが実際にstationsに存在するか
        if (basinIdSetting && currentStationIds.find((id) => id === basinIdSetting.obserId)) {
          return basinIdSetting.obserId;
        }
      }
    }
    const cookieObserId = getCookieValue('obserId');
    if (cookieObserId) return cookieObserId;

    return currentStationIds[0];
  }

  /**
   * 音声ファイルを再生する
   */
  playSoundFile(soundFileUrl: string) {
    try {
      const audio = new Audio(soundFileUrl);
      audio.play().catch((error) => {
        console.error('音声ファイルの再生に失敗しました:', error);
      });
    } catch (error) {
      console.error('音声ファイルの読み込みに失敗しました:', error);
    }
  }

  @Watch('autoLoad')
  async autoLoadWatcher() {
    if (this.autoLoad && !this['$store'].getters.intervalId) {
      const intervalId = setInterval(async () => {
        const { data: lastTime } = await lastTimeRepository.get({
          pathParams: {
            basinId: this.basinId,
            stationId: this.stationId,
          },
          queryParams: {
            isEn: false,
          },
        });
        const formattedLastTime = formatDateTimeToYYYYMMDDHHMM(lastTime.lastTime);
        // 自動更新によってresultの再フェッチが行われる場合のみ音声再生可能とする
        if (formattedLastTime !== this.targetTime) {
          this.canPlaySound = true;
        } else {
          this.canPlaySound = false;
        }
        this['$store'].commit('setLatestTargetTime', lastTime.lastTime);
        this['$store'].commit('setTargetTime', lastTime.lastTime);

        if (this.showEnsample) {
          const { data: enLastTime } = await lastTimeRepository.get({
            pathParams: {
              basinId: this.basinId,
              stationId: this.stationId,
            },
            queryParams: {
              isEn: true,
            },
          });
          this['$store'].commit('setEnLatestTargetTime', enLastTime.lastTime);
          this['$store'].commit('setEnTargetTime', enLastTime.lastTime);
        }
      }, 60000);
      // token refresh
      await authRepository.refresh();
      this['$store'].commit('setIntervalId', intervalId);
    }
    if (!this.autoLoad && this['$store'].getters.intervalId) {
      this.canPlaySound = false;
      clearInterval(this['$store'].getters.intervalId);
      this['$store'].commit('setIntervalId', null);
    }
  }

  @Watch('stationId')
  @Watch('tabId')
  loadStorage() {
    if (this.tabId === 'multiStations') {
      this['$store'].commit('setKouuScale', Number(localStorage.getItem('compare-water-KouuScale'))
        || '自動');
      this['$store'].commit('setRuikaScale', Number(localStorage.getItem('compare-water-RuikaScale'))
        || '自動');
      this['$store'].commit('setMaxHScale', Number(localStorage.getItem('compare-water-MaxHScale'))
        || '自動');
      this['$store'].commit('setMinHScale', Number(localStorage.getItem('compare-water-MinHScale'))
        || '自動');
      this['$store'].commit('setMaxQScale', Number(localStorage.getItem('compare-water-MaxQScale'))
      || '自動');
      return;
    }
    this['$store'].commit('setKouuScale', Number(localStorage.getItem(`${this.basinId}-${this.stationId}-KouuScale`))
      || '自動');
    this['$store'].commit('setRuikaScale', Number(localStorage.getItem(`${this.basinId}-${this.stationId}-RuikaScale`))
      || '自動');
    this['$store'].commit('setMaxHScale', Number(localStorage.getItem(`${this.basinId}-${this.stationId}-MaxHScale`))
      || '自動');
    this['$store'].commit('setMinHScale', Number(localStorage.getItem(`${this.basinId}-${this.stationId}-MinHScale`))
      || '自動');
    this['$store'].commit('setMaxQScale', Number(localStorage.getItem(`${this.basinId}-${this.stationId}-MaxQScale`))
      || '自動');
  }

  @Watch('stationId')
  updateBasinId() {
    this['$store'].commit('setBasinId', this.getBasinId());
  }

  @Watch('basinId')
  @Watch('stationId')
  @Watch('targetTime')
  @Watch('forceUpdateResult')
  async updateResultByWatcher() {
    if (this.skipWatch) return;
    const telemeterStore = useTelemeterStore();
    const telemeterBasinStore = useTelemeterBasinStore();
    await this.sysInfoStore.fetch();
    const stateStore = useStateStore();

    const { code } = this.sysInfoStore.stationsMap[this.stationId];
    if (this.sysInfoStore.isObservationEnabled(this.tabId) && stateStore.isObservationSwitchOn) {
      await telemeterStore.fetch({
        code,
        baseTime: this.targetTime,
      });
      await telemeterBasinStore.fetch({
        code,
        baseTime: this.targetTime,
      });
    } else {
      telemeterStore.reset();
      telemeterBasinStore.reset();
    }
    this.updateResult();
    this.updateTable();
  }

  /**
   * @description subResultの情報をもとにbasinIdを取得
   */
  getBasinId() {
    const { currentContent } = this['$store'].getters;
    // basinIdは事前放流のtoggleによって初期化
    const content = this.sysInfoStore.contents.find((it) => it.id === ContentsId.AutoCalculation);
    let basinId = currentContent.resultKey;

    // subResultが有効な場合のみ
    if (content && currentContent.subResult?.enable) {
      // defaultViewがあれば優先、なければtrue
      const isJizenHoryu = content.subResult?.defaultView ?? true;
      this['$store'].commit('setIsJizenHouryu', isJizenHoryu);
      if (currentContent.subResult?.enable && !isJizenHoryu) {
        basinId = this['$store'].getters.currentContent?.subResult?.resultKey;
      }
    }
    return basinId;
  }

  async updateTable() {
    const hasStatusChartContent = this.contents.some((content) => content.id === 'statusChart');
    if (hasStatusChartContent) {
      const { data: tableInfo } = await tableInfoRepository.get({
        pathParams: { basinId: this.basinId },
        queryParams: { tgttime: this.targetTime },
      });
      this['$store'].commit('setTableInfo', tableInfo);
    }
  }

  async updateResult() {
    if (!this.stationId) return;
    if (this.basinId && this.targetTime) {
      this.loading = true;
      try {
        loadKokiFloodReleaseSupportInfo({
          basinId: this.basinId, stationId: this.stationId, tgttime: this.targetTime,
        });
        const { data: result } = await damSupportInfoRepository.get({
          pathParams: {
            basinId: this.basinId,
            stationId: this.stationId,
          },
          queryParams: {
            tgttime: this.targetTime,
            isEn: this.tabId === 'ensembleCalculation',
          },
        });
        this['$store'].commit('setSupportInfoList', result);
      } catch (error) {
        this.loading = false;
        this['$store'].commit('setSupportInfoList', []);
      }
      try {
        const { data: dischargeInfo } = await dischargeInfoRepository.get({
          pathParams: {
            basinId: this.basinId,
            stationId: this.stationId,
          },
          queryParams: {
            tgttime: this.targetTime,
          },
        });
        if (Object.keys(dischargeInfo).length === 0) {
          this['$store'].commit('setDischargeInfo', null);
        } else {
          this['$store'].commit('setDischargeInfo', dischargeInfo);
        }
      } catch (error) {
        this.loading = false;
        this['$store'].commit('setDischargeInfo', null);
      }
      if (this.tabId === 'multiStations') {
        await this.getSuiiResultList();
      }
      try {
        await this.resultStore.fetch(this.basinId, this.stationId, this.targetTime);
        // 自動更新時の音声再生 （https://redmine.riskma.jp/issues/366）
        if (this.autoLoad && this.canPlaySound && !!this.resultStore.result.alertText) {
          this.playSoundFile(this.resultStore.result.alertText);
          this.canPlaySound = false;
        }
      } catch (error) {
        this.loading = false;
        this.resultStore.clear();
      }

      const simTableStore = useSimTableStore();
      if (this.sysInfoStore.contents.find((it) => it.id === ContentsId.AutoCalculation)?.isFloodControlEffect) {
        try {
          simTableStore.clear();
          await simTableStore.fetch(this.basinId, this.stationId, this.targetTime);
        } catch (error) {
          console.info('↑ simTable fetch error');
        }
      }
      this.loading = false;
    }
  }

  async getSuiiResultList() {
    const stationIds = this['$store'].getters.currentStationIds;
    const resultList: any = [];
    for (let i = 0; i < stationIds.length; i += 1) {
      // eslint-disable-next-line no-await-in-loop
      const data = await this.resultStore.fetchOnly(this['$store'].getters.currentContent.resultKey, stationIds[i], this.targetTime);
      resultList.push(data);
    }
    this['$store'].commit('setSuiiResultList', resultList);
  }

  // TODO: updateResultと同じでwatchを分離した方がよさそう, 複数回呼び出される？
  @Watch('stationId')
  @Watch('enTargetTime')
  async updateEnResult() {
    if (this.tabId !== 'ensembleCalculation') return;
    if (this.stationId && this.basinId && this.enTargetTime && this.sysInfoStore.getSelectableStations(this.tabId).includes(this.stationId)) {
      try {
        this['$store'].commit('setEnsampleTabStatus', 'loading');
        const result = await this.resultStore.fetchOnly(this.basinId, this.stationId, this.enTargetTime, true);
        const enResult = result.simResults ?? [];
        // 累加雨量でソート
        enResult.sort(
          (res: any[], res2: any[]) => res2[res2.length - 1].ruika - res[res.length - 1].ruika,
        );
        this['$store'].commit('setEnResult', enResult);
        if (enResult.length > 0) {
          this['$store'].commit('setEnsampleTabStatus', 'show');
        } else {
          this['$store'].commit('setEnsampleTabStatus', 'hide');
        }
      } catch (error) {
        this['$store'].commit('setEnsampleTabStatus', 'hide');
      }
    }
  }

  @Watch('tabId')
  async setUpContentsByWatcher() {
    // resultKeyが存在する場合のみ
    if (!this['$store'].getters.currentContent?.resultKey) return;
    this.skipWatch = true;
    this.loading = true;
    await this.setUpContents();
    if (this.tabId !== 'ensembleCalculation' && this.tabId !== 'meshMap') {
      await this.updateResult();
    } if (this.tabId === 'ensembleCalculation') {
      await this.updateEnResult();
    } if (this.tabId === 'statusChart') {
      await this.updateTable();
    }
    this.loading = false;
    this.skipWatch = false;
  }

  async setUpContents() {
    if (this.isMobile) {
      this['$store'].commit('setIsMobile', true);
    }

    try {
      let { stationId } = this;
      await this.sysInfoStore.fetch();
      const telemeterStore = useTelemeterStore();
      const telemeterBasinStore = useTelemeterBasinStore();
      const stateStore = useStateStore();

      if (this.sysInfoStore.projectKey === ProjectKey.ToneLowFlow) {
        const { data: { kakuho_date_map: kurihashiKakuhoMap } } = await kurihashiKakuhoRepository
          .get();
        this['$store'].commit('setKurihashiKakuhoMap', kurihashiKakuhoMap);
      }
      this['$store'].commit('setHelpTeam', this.sysInfoStore?.helpTeam ?? null);
      this['$store'].commit('setHeader', this.sysInfoStore.header);
      this['$store'].commit('setContents', this.sysInfoStore.contents ?? []);
      this['$store'].commit('setColors', this.sysInfoStore.colors);

      const basinId = this.getBasinId();

      // TODO: 利根川（低水）専用対応
      // if (this.tabId === 'multiStations') {
      //   const { multiStations: { stations } } = sysInfo.basinStationsMap['02tonelowflow'];
      //   const [station] = stations;
      //   stationId = station;
      // }
      if (this.tabId !== 'ensembleCalculation') {
        stationId = this.getDefaultStationId(this.currentStationIds, basinId);
      } else if (this.currentStationIds.length > 0) {
        stationId = this.currentStationIds.length && this.currentStationIds[0];
      } else {
        throw new Error('stationIds not found.');
      }

      await logRepository.log({
        action: LogAction.ChangeObservatory,
        params: {
          stationId,
          stationName: this.sysInfoStore.stationsMap[stationId].name,
        },
      });

      const { data: lastTime } = await lastTimeRepository.get({
        pathParams: { basinId, stationId },
        queryParams: { isEn: false },
      });

      this['$store'].commit('setStationsMap', this.sysInfoStore.stationsMap);
      const { scales, type } = this.sysInfoStore.stationsMap[stationId];
      this['$store'].commit('setMaxHScales', scales.maxHList);
      this['$store'].commit('setMinHScales', scales.minHList);
      this['$store'].commit('setMaxQScales', scales.maxQList);
      this['$store'].commit('setType', type);
      if (this.showEnsample) {
        this['$store'].commit('setEnTargetTime', null);
      }
      this['$store'].commit('setTargetTime', lastTime.lastTime);
      this['$store'].commit('setLatestTargetTime', lastTime.lastTime);
      this['$store'].commit('setBasinId', basinId);

      const { code } = this.sysInfoStore.stationsMap[stationId];
      if (this.sysInfoStore.isObservationEnabled(this.tabId) && stateStore.isObservationSwitchOn) {
        await telemeterStore.fetch({
          code,
          baseTime: lastTime.lastTime,
        });
        await telemeterBasinStore.fetch({
          code,
          baseTime: lastTime.lastTime,
        });
      } else {
        telemeterStore.reset();
        telemeterBasinStore.reset();
      }

      if (this.showEnsample) {
        const { data: enLastTime } = await lastTimeRepository.get({
          pathParams: {
            basinId,
            stationId,
          },
          queryParams: {
            isEn: true,
          },
        });
        this['$store'].commit('setEnLatestTargetTime', enLastTime.lastTime);
        this['$store'].commit('setEnTargetTime', enLastTime.lastTime);
      }
      this['$store'].commit('setStationId', stationId);
      this.loadStorage();
    } catch (error) {
        this.resultStore.clear();
        // @ts-ignore
        if (error?.response?.status === 401) {
          this['$router'].push('/login');
        }
    }
  }

  @Watch('loading')
  async setMarginTop() {
    setTimeout(() => {
      const element = document.getElementById('notice');
      if (element !== null) {
        const height = element.clientHeight;
        this.marginTop = `${height}px`;
      }
    }, 500);
    if (!this.loading) {
      const { stationId } = this['$route'].query;
      if (stationId) {
        this.stationId = stationId;
        this.loading = true;
        await this.updateResult();
        this['$router'].replace('/');
        this.loading = false;
      }
    }
  }

  @Watch('header')
  changeTitle() {
    document.title = this.header.name;
  }

  async created() {
    await this.setUpContents();
    await this.updateResult();
    await this.updateEnResult();

    if (this.sysInfoStore.projectKey === ProjectKey.ToneLowFlow) {
      this['$store'].commit('setYskSpan', 48);
    }
    this.loading = false;
    this.skipWatch = false;
  }
}
